import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { appConfigStore } from 'Models/AppConfigStore';
import { UserEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import UserEntitlementsWrapper from 'Modules/AnytimeTicket/Components/UserEntitlements/UserEntitlementsWrapper';
import CommunicationPreferencesSection from 'Modules/CommunicationPreferences/Components/CommunicationPreferencesSection';
import { cancelChangeEmailRequest, changeEmailRequest, me } from 'Services/Api/AccountService';
import alertToast from 'Util/ToastifyUtils';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import AccountDetailsVouchers from 'Views/Components/_HumanWritten/AccountDetails/VoucherSection/GiftVouchersWrapper';
import AccountDetailsVehicles from 'Views/Components/_HumanWritten/AccountDetails/VehiclesSection/AccountDetailsVehicles';
import DetailFields from 'Views/Components/_HumanWritten/AccountDetails/AccountFields/DetailFields';
import { saveUser } from 'Views/Components/_HumanWritten/AccountDetails/AccountFields/AccountFieldsUtils';
import { UserInvoiceListComponent } from 'Views/Components/_HumanWritten/UserInvoiceList/UserInvoiceListComponent';
import GuestTag from 'Views/Components/_HumanWritten/Tag/GuestTag';
import { UserDisplayNameTag } from 'Views/Components/_HumanWritten/UserDisplayNameTag';
import { useCanEditUser } from '../Hooks/useCanEditUser';
import { useAccountEditSession } from '../Hooks/useAccountEditSession';
import { useConsumeEditModeParam } from '../Hooks/useConsumeEditModeParam';
import { useTabState } from '../Hooks/useTabState';
import AccountPageShell from './AccountPageShell';
import NewEmailUnverifiedBanner from './NewEmailUnverifiedBanner';
import { anytimeTicketsLabel } from 'Util/_HumanWritten/anytimeTicketTerm';

export interface CustomerAccountInnerProps {
	user: UserEntity;
	refetchUser: () => void;
	initialTab: string;
	defaultMode?: EntityFormMode;
}

// Only the Account tab participates in the edit session on the customer page.
const EDIT_SESSION_TABS = ['account'];

/**
 * The customer's own account page (/account-details/:tab?) on the same shell as the staff user
 * detail page — Account / Credits / Anytime Tickets / Vehicles / Invoices. Only the Account tab
 * is editable (session shared with UserDetailInner via useAccountEditSession), and the save
 * path is the customer one: save, refresh the logged-in store user, and stage any email change
 * through a self-service verification request.
 */
export default function CustomerAccountInner({
	user,
	refetchUser,
	initialTab,
	defaultMode = EntityFormMode.VIEW,
}: CustomerAccountInnerProps) {
	const intl = useIntl();
	// isTargetStaff covers staff members opening their own profile — they only get the
	// customer-agnostic tabs (same gating the legacy page applied).
	const { isTargetStaff } = useCanEditUser(user);

	const [mode, setMode] = useState<EntityFormMode>(defaultMode);
	const [cancellingEmailChange, setCancellingEmailChange] = useState(false);

	// A ?mode=edit deep link only opens the session; it must not survive into the address bar,
	// or the remount that Cancel/Save triggers would put the page straight back into edit mode.
	useConsumeEditModeParam(defaultMode === EntityFormMode.EDIT);

	const tabs = React.useMemo(() => {
		const items = [{ id: 'account', label: 'Account' }];
		if (appConfigStore.showCreditOnCustomerAccount) items.push({ id: 'credits', label: 'Credits' });
		if (!isTargetStaff && appConfigStore.anytimeTicketingEnabled) {
			items.push({ id: 'entitlements', label: anytimeTicketsLabel() });
		}
		if (!isTargetStaff && appConfigStore.vehicleEnabled && !user.isGuest) {
			items.push({
				id: 'vehicles',
				label: `${intl.formatMessage({ id: 'vehicle__name__label', defaultMessage: 'Vehicle' })}s`,
			});
		}
		if (!isTargetStaff && !store.isStaff) items.push({ id: 'invoices', label: 'Invoices' });
		// Guests are excluded: the account is transient and cannot be signed into again, so a guest
		// has no way back to a choice made here. Their only opt-out is replying STOP to a text.
		if (!user.isGuest) items.push({ id: 'communications', label: 'Communications' });
		return items;
	}, [isTargetStaff, user.isGuest, intl]);

	const [activeTab, setTab] = useTabState({
		tabs,
		initialTab,
		fallbackId: 'account',
		mode,
		urlFor: tabId => `/account-details/${tabId}`,
		editSessionTabs: EDIT_SESSION_TABS,
		unknownInitialTab: 'fallback',
	});

	const session = useAccountEditSession({
		user,
		refetchUser,
		// Customer self-save: refresh the logged-in store user, then stage the email change as
		// a self-service verification request. The success toast comes after everything has
		// been awaited — a failed email-change request must not read as "Details saved!".
		performSave: async ({ emailHasChanged, submitUser, newEmail, clearChangesMade }) => {
			await saveUser(submitUser, user, store.isManager);
			clearChangesMade();

			const userResult = await me();
			store.setLoggedInUser(userResult);
			if (emailHasChanged) {
				await changeEmailRequest(newEmail);
			}
			refetchUser();
			alertToast('Your details have been updated!', 'success', 'Details saved!');
		},
	});
	const { form, errors, saving, handleSave, cancelEdit } = session;

	// Leave edit mode as soon as the discard lands — the refetch remount confirms with fresh
	// server data moments later, but the page must not sit in edit mode waiting for it.
	const handleCancel = async () => {
		if (await cancelEdit()) {
			setMode(EntityFormMode.VIEW);
		}
	};

	const headerTitle = (
		<span className="flex flex-wrap items-center gap-2">
			{user.getFullName()}
			{UserDisplayNameTag(user?.userDisplayName?.displayName)}
			{user.isGuest && <GuestTag />}
		</span>
	);

	const subtitleParts = [user.email, appConfigStore.userPhoneEnabled && user.phone ? user.phone : null].filter(
		Boolean,
	);

	const cancelEmailUpdateButton = (
		<Button
			newStyles
			display={Display.Outline}
			colors={Colors.Secondary}
			sizes={Sizes.Small}
			disabled={cancellingEmailChange}
			buttonProps={{ 'data-test-id': 'account-cancel-email-update' } as any}
			onClick={async () => {
				setCancellingEmailChange(true);
				try {
					await cancelChangeEmailRequest(user.id);
					refetchUser();
					alertToast('Cancelled email change request', 'success');
				} catch {
					alertToast('Could not cancel email change request', 'error');
				} finally {
					setCancellingEmailChange(false);
				}
			}}
		>
			Cancel update
		</Button>
	);

	const renderActiveTab = () => {
		switch (activeTab) {
			case 'credits':
				return <AccountDetailsVouchers user={user} />;
			case 'entitlements':
				return <UserEntitlementsWrapper user={user} />;
			case 'vehicles':
				return <AccountDetailsVehicles user={user} />;
			case 'invoices':
				return <UserInvoiceListComponent userId={user.id} />;
			case 'communications':
				return <CommunicationPreferencesSection />;
			case 'account':
			default:
				return <DetailFields user={user} form={form} mode={mode} setMode={setMode} errors={errors} />;
		}
	};

	return (
		<AccountPageShell
			className="customer-account-page"
			title={headerTitle}
			subtitle={subtitleParts.join(' · ')}
			mode={mode}
			saving={saving}
			onSave={handleSave}
			onCancel={handleCancel}
			banners={<NewEmailUnverifiedBanner user={user} actions={cancelEmailUpdateButton} />}
			tabs={tabs}
			activeTab={activeTab}
			onTabChange={setTab}
			editSessionTabs={EDIT_SESSION_TABS}
		>
			{renderActiveTab()}
		</AccountPageShell>
	);
}
